import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { 
  BarChart3, 
  Users, 
  DollarSign, 
  Activity, 
  TrendingUp, 
  AlertCircle,
  RefreshCw,
  Calendar,
  User,
  Loader2
} from 'lucide-react';
import LLMAnalyticsTable from './LLMAnalyticsTable';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { resolveEmails } from '../services/emailResolver';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const LLMAnalytics = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState([]);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [detailsPage, setDetailsPage] = useState(0);
  const [detailsLimit] = useState(10);
  const [totalDetails, setTotalDetails] = useState(0);
  
  // Email resolution state
  const [emailMap, setEmailMap] = useState(new Map());
  const [emailsLoading, setEmailsLoading] = useState(false);
  const [emailErrors, setEmailErrors] = useState(new Map());
  
  // Role detection
  const isSuperAdmin = user?.is_superadmin || user?.role === 'superadmin';

  const periodOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'all', label: 'All time' }
  ];

  // Email resolution function
  const resolveUserEmails = async (userIds) => {
    if (!isSuperAdmin || !userIds || userIds.length === 0) {
      return;
    }

    setEmailsLoading(true);
    try {
      const resolvedEmails = await resolveEmails(userIds);
      setEmailMap(prevMap => {
        const newMap = new Map(prevMap);
        resolvedEmails.forEach((email, userId) => {
          newMap.set(userId, email);
        });
        return newMap;
      });
    } catch (error) {
      console.error('Email resolution failed:', error);
      toast.error('Some emails could not be loaded', {
        role: 'alert'
      });
    } finally {
      setEmailsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, [period, selectedUserId, selectedModel, detailsPage]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [summaryResponse, userAnalyticsResponse, modelsResponse, detailsResponse] = await Promise.all([
        fetchSummary(),
        fetchUserAnalytics(),
        fetchModels(),
        fetchDetails()
      ]);
      
      // Trigger email resolution after data is loaded
      if (isSuperAdmin) {
        const allUserIds = new Set();
        
        // Collect user_ids from userAnalytics response
        if (userAnalyticsResponse && userAnalyticsResponse.users) {
          userAnalyticsResponse.users.forEach(user => {
            if (user.user_id) allUserIds.add(user.user_id);
          });
        }
        
        // Collect user_ids from details response
        if (detailsResponse && detailsResponse.records) {
          detailsResponse.records.forEach(detail => {
            if (detail.user_id) allUserIds.add(detail.user_id);
          });
        }
        
        // Resolve emails for all unique user_ids
        if (allUserIds.size > 0) {
          resolveUserEmails(Array.from(allUserIds));
        }
      }
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };


  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/llm-analytics/summary`, {
        params: { period }
      });
      setSummary(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch summary:', error);
      throw error;
    }
  };

  const fetchUserAnalytics = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/llm-analytics/by-user`, {
        params: { period, limit: 50 }
      });
      setUserAnalytics(response.data.users || []);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user analytics:', error);
      throw error;
    }
  };

  const fetchModels = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/llm-analytics/models`, {
        params: { period }
      });
      setAvailableModels(response.data.models || []);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch models:', error);
      throw error;
    }
  };

  const fetchDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/llm-analytics/details`, {
        params: { 
          period, 
          user_id: selectedUserId || undefined,
          model: selectedModel || undefined,
          limit: detailsLimit,
          offset: detailsPage * detailsLimit
        }
      });
      setDetails(response.data.records || []);
      setTotalDetails(response.data.total_count || 0);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch details:', error);
      throw error;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 4
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatDate = (dateString) => {
    // Ensure the date string is treated as UTC if it doesn't have timezone info
    const date = new Date(dateString);
    
    // Convert UTC to IST (UTC + 5:30)
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));
    
    return istDate.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }) + ' IST';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      success: { color: 'bg-green-100 text-green-800', icon: '✓' },
      failed: { color: 'bg-red-100 text-red-800', icon: '✗' },
      cost_unavailable: { color: 'bg-yellow-100 text-yellow-800', icon: '?' }
    };
    
    const config = statusConfig[status] || statusConfig.failed;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.icon} {status}
      </span>
    );
  };

  const summaryColumns = [
    { key: 'metric', label: 'Metric' },
    { key: 'value', label: 'Value' }
  ];

  const userColumns = [
    { 
      key: 'user_id', 
      label: isSuperAdmin ? 'User Email' : 'User ID',
      render: (value, row) => {
        const displayValue = isSuperAdmin 
          ? (emailMap.get(value) || (emailMap.get(value) === null ? `${value} (deleted)` : value))
          : value;
        
        return (
          <div className="flex items-center">
            <User className="w-4 h-4 mr-2 text-gray-400" />
            {emailsLoading && isSuperAdmin ? (
              <div className="flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </div>
            ) : (
              displayValue
            )}
          </div>
        );
      }
    },
    { 
      key: 'total_cost', 
      label: 'Total Cost',
      render: (value) => formatCurrency(value)
    },
    { 
      key: 'call_count', 
      label: 'Calls',
      render: (value) => formatNumber(value)
    },
    { 
      key: 'total_tokens', 
      label: 'Total Tokens',
      render: (value) => formatNumber(value)
    }
  ];

  const detailsColumns = [
    { 
      key: 'timestamp', 
      label: 'Timestamp',
      render: (value) => formatDate(value)
    },
    { 
      key: 'user_id', 
      label: isSuperAdmin ? 'User Email' : 'User ID',
      render: (value) => {
        const displayValue = isSuperAdmin 
          ? (emailMap.get(value) || (emailMap.get(value) === null ? `${value} (deleted)` : value))
          : value;
        
        return (
          <div className="flex items-center">
            <User className="w-4 h-4 mr-2 text-gray-400" />
            {emailsLoading && isSuperAdmin ? (
              <div className="flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </div>
            ) : (
              displayValue
            )}
          </div>
        );
      }
    },
    { 
      key: 'model', 
      label: 'Model',
      render: (value) => (
        <span className="font-mono text-xs bg-purple-50 text-purple-700 px-1 py-0.5 rounded border border-purple-200">
          {value || 'unknown'}
        </span>
      )
    },
    { 
      key: 'endpoint', 
      label: 'Endpoint',
      render: (value) => (
        <span className="font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">
          {value}
        </span>
      )
    },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => getStatusBadge(value)
    },
    { 
      key: 'cost', 
      label: 'Cost',
      render: (value) => value ? formatCurrency(value) : '-'
    },
    { 
      key: 'total_tokens', 
      label: 'Tokens',
      render: (value) => formatNumber(value)
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold font-display mb-2">
              <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                LLM Analytics
              </span>{' '}
              <span>📊</span>
            </h1>
            <p className="text-lg text-slate-600">
              Monitor AI usage, costs, and performance across the platform
            </p>
          </div>
          <Button 
            onClick={fetchAllData}
            disabled={loading}
            className="btn-hover gradient-emerald text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card className="glass border-0">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center space-x-4">
                <Calendar className="w-5 h-5 text-emerald-600" />
                <Label htmlFor="period-select">Time Period:</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {periodOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {availableModels.length > 0 && (
                <div className="flex items-center space-x-4">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  <Label htmlFor="model-select">Model:</Label>
                  <Select value={selectedModel || "all"} onValueChange={(value) => {
                    setSelectedModel(value === "all" ? "" : value);
                    setDetailsPage(0);
                  }}>
                    <SelectTrigger className="w-64">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All models</SelectItem>
                      {availableModels.map((model) => (
                        <SelectItem key={model} value={model}>
                          {model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="glass border-0">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Total Cost</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      {formatCurrency(summary.total_cost)}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-emerald-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-0">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Total Calls</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatNumber(summary.total_calls)}
                    </p>
                  </div>
                  <Activity className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-0">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Success Rate</p>
                    <p className="text-2xl font-bold text-green-600">
                      {summary.total_calls > 0 
                        ? Math.round((summary.successful_calls / summary.total_calls) * 100)
                        : 0}%
                    </p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass border-0">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600">Total Tokens</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {formatNumber(summary.total_input_tokens + summary.total_output_tokens)}
                    </p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Summary Table */}
        {summary && (
          <LLMAnalyticsTable
            title="Summary Statistics"
            columns={summaryColumns}
            data={[
              { metric: 'Total Calls', value: formatNumber(summary.total_calls) },
              { metric: 'Successful Calls', value: formatNumber(summary.successful_calls) },
              { metric: 'Failed Calls', value: formatNumber(summary.failed_calls) },
              { metric: 'Cost Unavailable', value: formatNumber(summary.cost_unavailable_calls) },
              { metric: 'Total Cost', value: formatCurrency(summary.total_cost) },
              { metric: 'Input Tokens', value: formatNumber(summary.total_input_tokens) },
              { metric: 'Output Tokens', value: formatNumber(summary.total_output_tokens) }
            ]}
          />
        )}

        {/* User Analytics */}
        <LLMAnalyticsTable
          title={`User Analytics (${period})`}
          columns={userColumns}
          data={userAnalytics}
        />

        {/* Details Section */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="w-6 h-6 text-emerald-600" />
              <span>Detailed Usage Records</span>
            </CardTitle>
            <CardDescription>
              Individual LLM API calls with filtering and pagination
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* User Filter */}
              <div className="flex items-center space-x-4">
                <Label htmlFor="user-filter">Filter by User:</Label>
                <Select value={selectedUserId || "all"} onValueChange={(value) => {
                  setSelectedUserId(value === "all" ? "" : value);
                  setDetailsPage(0);
                }}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {userAnalytics.map((user) => {
                      const displayValue = isSuperAdmin 
                        ? (emailMap.get(user.user_id) || (emailMap.get(user.user_id) === null ? `${user.user_id} (deleted)` : user.user_id))
                        : user.user_id;
                      
                      return (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {displayValue}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Details Table */}
              <LLMAnalyticsTable
                title={`Usage Details (${formatNumber(totalDetails)} total records)`}
                columns={detailsColumns}
                data={details}
              />

              {/* Pagination */}
              {totalDetails > detailsLimit && (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-slate-600">
                      Showing <span className="font-semibold text-emerald-600">{detailsPage * detailsLimit + 1}</span> to <span className="font-semibold text-emerald-600">{Math.min((detailsPage + 1) * detailsLimit, totalDetails)}</span> of <span className="font-semibold text-emerald-600">{formatNumber(totalDetails)}</span> records
                    </p>
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDetailsPage(0)}
                        disabled={detailsPage === 0}
                        className="px-3"
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDetailsPage(Math.max(0, detailsPage - 1))}
                        disabled={detailsPage === 0}
                        className="px-3"
                      >
                        ‹
                      </Button>
                      
                      {/* Page Numbers */}
                      {(() => {
                        const totalPages = Math.ceil(totalDetails / detailsLimit);
                        const maxPagesToShow = 5;
                        let startPage = Math.max(0, detailsPage - Math.floor(maxPagesToShow / 2));
                        let endPage = Math.min(totalPages, startPage + maxPagesToShow);
                        
                        if (endPage - startPage < maxPagesToShow) {
                          startPage = Math.max(0, endPage - maxPagesToShow);
                        }
                        
                        const pages = [];
                        for (let i = startPage; i < endPage; i++) {
                          pages.push(
                            <Button
                              key={i}
                              variant={i === detailsPage ? "default" : "outline"}
                              size="sm"
                              onClick={() => setDetailsPage(i)}
                              className={i === detailsPage ? "bg-emerald-600 text-white hover:bg-emerald-700 px-3" : "px-3"}
                            >
                              {i + 1}
                            </Button>
                          );
                        }
                        return pages;
                      })()}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDetailsPage(detailsPage + 1)}
                        disabled={(detailsPage + 1) * detailsLimit >= totalDetails}
                        className="px-3"
                      >
                        ›
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDetailsPage(Math.ceil(totalDetails / detailsLimit) - 1)}
                        disabled={(detailsPage + 1) * detailsLimit >= totalDetails}
                        className="px-3"
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LLMAnalytics;
